<script lang="ts">
  import { untrack } from "svelte"
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiArrowLeft, mdiArrowRight, mdiMagnify } from "@mdi/js"
  import { location } from "../../../../../../_shared/_logic/store"
  import { spaLink, spaNavigate } from "../../../../../../_shared/_logic/navigation"
  import { buildLearningPages, learningBasePath } from "../../config"
  import {
    getLearningLessonHref,
    getLearningOverviewHref,
  } from "../../../../_shared/_logic/learning-path"
  import { getLearningProgress } from "../../../../_shared/_logic/learning-progress"
  import { getOrganizationNavigationMetadata } from "../../../../context"
  import { resolveLearningEntryDestinations } from "./_logic/entryTargets"
  import { getGuideQuickTasks, resolveGuideSelection, searchGuideLessons } from "./_logic/selection"
  import LessonContent from "./_modules/lesson-content"
  import CourseOverview from "./_modules/course-overview"
  import ChapterNavigation from "./_modules/chapter-navigation"

  const { organization, userId } = $props<{ organization: string; userId: string }>()
  const progress = getLearningProgress()
  const course = $derived(progress.course)
  const loading = $derived(progress.loading)
  const error = $derived(progress.error)
  const savingLessonKey = $derived(progress.savingLessonKey)
  const completionError = $derived(progress.completionError)
  const loadCourse = () => progress.loadCourse()
  const saveCompletion = (key: string, completed: boolean, version: number) =>
    progress.saveCompletion(key, completed, version)
  const basePath = $derived(learningBasePath(organization))
  const getNavigationMetadata = getOrganizationNavigationMetadata()
  const entryDestinations = $derived(
    resolveLearningEntryDestinations(
      getNavigationMetadata?.(),
      `/organizations/${encodeURIComponent(organization)}`,
    ),
  )
  const entryDestinationsLoading = $derived(
    Boolean(getNavigationMetadata && !getNavigationMetadata().ready),
  )
  const overviewHref = $derived(getLearningOverviewHref(basePath, $location.search))
  let search = $state({ path: "", value: "" })
  const query = $derived(search.path === $location.path ? search.value : "")
  const selection = $derived(
    course ? resolveGuideSelection(course, basePath, $location.path) : null,
  )
  const results = $derived(searchGuideLessons(course?.lessons ?? [], query))
  const quickTasks = $derived(getGuideQuickTasks(course?.lessons ?? []))
  const pages = $derived(buildLearningPages(basePath, content))

  $effect(() => {
    const redirect = selection?.redirect

    if (redirect) untrack(() => spaNavigate(redirect, { replace: true, noSearchParam: false }))
  })
</script>

{#snippet chapterSearch()}
  <label class="guide-search">
    <Icon path={mdiMagnify} size="1.2rem" />
    <span class="sr-only">Anleitung suchen</span>
    <input
      type="search"
      placeholder="z. B. Urlaub planen oder Brief drucken"
      value={query}
      oninput={(event) => (search = { path: $location.path, value: event.currentTarget.value })}
    />
  </label>
{/snippet}

{#snippet taskShortcuts()}
  <nav class="task-shortcuts" aria-label="Anleitungen für häufige Aufgaben">
    {#each quickTasks as task (task.lesson.key)}
      <a use:spaLink href={getLearningLessonHref(basePath, task.lesson, $location.search)}>
        <span>{task.label}</span><Icon path={mdiArrowRight} size="1.1rem" />
      </a>
    {/each}
  </nav>
{/snippet}

{#snippet searchResults()}
  <section class="guide-results" aria-label="Suchergebnisse">
    <h2>Suchergebnisse ({results.length})</h2>
    {#each results as result (result.key)}
      <a
        use:spaLink
        href={getLearningLessonHref(basePath, result, $location.search)}
        onclick={() => (search = { path: $location.path, value: "" })}
      >
        <span>{result.title}</span>
        <p>{result.summary}</p>
        <small>{result.topic_title}{result.completed ? " · Erledigt" : ""}</small>
      </a>
    {:else}
      <p
        >Keine passende Anleitung gefunden. Versuchen Sie einen kurzen Begriff wie „Urlaub“, „Brief“ oder „Telefonnummer“.</p
      >
    {/each}
  </section>
{/snippet}

{#snippet content()}
  <div class="guide" aria-busy={loading}>
    {#if selection?.lesson}
      <a class="guide-back" href={overviewHref} use:spaLink
        ><Icon path={mdiArrowLeft} size="1rem" />Zur Lernübersicht</a
      >
    {/if}
    {#if completionError}
      <p role="alert">{completionError.message}</p>
    {/if}
    {#if loading && !course}
      <p role="status">Anleitungen werden geladen …</p>
    {:else if error}
      <div role="alert">
        <p>Die Anleitungen konnten nicht geladen werden.</p>
        <CTA
          type="secondaryButton"
          content="Erneut laden"
          darkMode={false}
          onClick={() => void loadCourse()}
        />
      </div>
    {:else if course && !course.lessons.length}
      <p>Noch keine Anleitungen verfügbar.</p>
    {:else if course && selection?.overview}
      <CourseOverview
        {course}
        {basePath}
        search={$location.search}
        {chapterSearch}
        {taskShortcuts}
        searching={Boolean(query.trim())}
      />
      {#if query.trim()}
        {@render searchResults()}
      {:else}
        <h2 class="directory-title">Alle Anleitungen</h2>
        <ChapterNavigation {course} {basePath} search={$location.search} />
      {/if}
    {:else if course && selection?.lesson}
        <div class="guide-lesson">
          {#key selection.lesson.key}
            <LessonContent
            lessonKey={selection.lesson.key}
            {course}
            {basePath}
            {entryDestinations}
            {entryDestinationsLoading}
            saving={savingLessonKey !== null}
            versionConflict={completionError?.lessonKey === selection.lesson.key &&
              completionError.versionConflict}
            onSetCompleted={saveCompletion}
            onReloadCourse={loadCourse}
          >
              {#snippet renderChapters(closeContents)}
                <h2 class="directory-title">Andere Anleitung finden</h2>
                {@render chapterSearch()}
                {#if query.trim()}
                  {@render searchResults()}
                {:else}
                <ChapterNavigation
                  {course}
                  {basePath}
                  search={$location.search}
                  activeLessonKey={selection.lesson?.key}
                  onNavigate={closeContents}
                  compact
                />
                {/if}
              {/snippet}
            </LessonContent>
          {/key}
        </div>
    {:else if course && !selection?.redirect}
      <div role="alert">
        <p>Diese Anleitung wurde nicht gefunden.</p>
        <a use:spaLink href={basePath}>Zu den Anleitungen</a>
      </div>
    {/if}
  </div>
{/snippet}

<PageTemplate
  viewKey={`learning:${organization}:${userId}`}
  title=""
  pageNavigation={pages}
  hidePrimaryNavigationWhenSinglePage={true}
  locationStore={location}
  appBreadcrumbs={[
    { title: "KehrBlatt lernen", href: overviewHref },
    ...(selection?.lesson ? [{ title: selection.lesson.title }] : []),
  ]}
  testId="controlAdmin.learning"
/>

<style>
  .guide {
    min-width: 0;
    padding-bottom: 1.5rem;
    color: var(--text-invers-100);
    background: #fff;
    letter-spacing: 0;
    font-size: 18px;
  }
  .guide-back {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 1rem;
    color: inherit;
    font-size: 16px;
  }
  .guide-lesson {
    min-width: 0;
  }
  .guide-back:focus-visible {
    outline: 2px solid #7c2024;
    outline-offset: 2px;
  }
  .guide-search {
    display: flex;
    gap: 0.6rem;
    align-items: center;
    width: min(100%, 38rem);
    border-bottom: 1px solid currentColor;
    padding: 0.5rem 0;
  }
  .guide-search input {
    min-width: 0;
    width: 100%;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
  }
  .guide-search:focus-within {
    outline: 2px solid var(--primary-color, #7c2024);
    outline-offset: 4px;
  }
  .guide-results {
    max-width: 60rem;
  }
  .guide-results h2 {
    font-size: 22px;
    color: inherit;
  }
  .guide-results a {
    display: flex;
    flex-direction: column;
    font-weight: 400;
    gap: 0.35rem;
    padding: 1rem 0;
    border-bottom: 1px solid #d8dfe2;
    color: inherit;
  }
  .guide-results a span {
    font-weight: 600;
    line-height: 1.4;
  }
  .guide-results a p {
    margin: 0;
    max-width: 72ch;
    font-size: 16px;
    line-height: 1.5;
  }
  .directory-title {
    color: inherit;
    font-size: 21px;
    margin: 1.5rem 0 1rem;
  }
  .task-shortcuts {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0 1.5rem;
    margin: 0.5rem 0 1.75rem;
  }
  .task-shortcuts a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.65rem;
    min-height: 52px;
    padding: 0.65rem 0;
    border-bottom: 1px solid #d8dfe2;
    color: inherit;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.4;
    text-decoration: none;
  }
  .task-shortcuts a span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .task-shortcuts a:hover {
    color: #7c2024;
    text-decoration: underline;
  }
  .task-shortcuts a:focus-visible,
  .guide-results a:focus-visible {
    outline: 2px solid #7c2024;
    outline-offset: 3px;
  }
  @media (max-width: 1000px) {
    .task-shortcuts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 600px) {
    .task-shortcuts {
      grid-template-columns: minmax(0, 1fr);
    }
    .guide-search input {
      font-size: 16px;
    }
  }
  .guide-results small {
    font-size: 15px;
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
</style>
